import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Row,
  Col,
  Badge,
  Table,
  Spinner,
  Alert,
  Button
} from "reactstrap";
import { 
  Calendar, 
  Clock, 
  User, 
  MapPin, 
  Wrench, 
  Users, 
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { ClipLoader } from "react-spinners";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { useNavigate } from "react-router-dom";

// Import existing APIs - NO CHANGES TO EXISTING FUNCTIONALITY
import { getBookings } from "../../components/Api/GmsBooking";
import { getCustomers } from "../../components/Api/GmsCustomer";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getRooms } from "../../components/Api/GmsRoom";
import { getEquipments } from "../../components/Api/GmsEquipment";
import { getSpaServices } from "../../components/Api/GmsSpaService";
import { getEmployeeIncentives } from "../../components/Api/GmsIncentive";

const SpaEmployeeDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [monthBookings, setMonthBookings] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    // Today's Schedule
    todaysBookings: [],
    currentStatus: 'Available',
    
    // Customer Information
    recentCustomers: [],
    repeatCustomers: [],
    
    // Equipment & Resources
    assignedEquipment: [],
    roomAssignments: []
  });

  // Add incentive data state
  const [incentiveData, setIncentiveData] = useState({
    performance_incentive: 0,
    sales_incentive: 0,
    total_incentive: 0,
    performance_message: '',
    sales_message: '',
    period_start: '',
    period_end: ''
  });

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaEmployeeDashboard');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsSpaEmployeeDashboard');
  const isAdmin = isUserAdmin();
  
  // Get current user's employee ID
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentEmployeeId = currentUser.user_EmployeeId || currentUser.employeeId;

  useEffect(() => {
    if (hasPermission && currentEmployeeId) {
      loadDashboardData();
      loadMonthBookings();
      loadIncentiveData();
    } else if (!hasPermission) {
      // Check if user has Spa Manager & Finance Dashboard permission
      const spaManagerFinancePermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaManagerFinanceDashboard');
      if (spaManagerFinancePermission) {
        navigate('/GmsSpaManagerFinanceDashboard');
        return;
      }
      navigate('/Dashboard');
    }
  }, [hasPermission, currentEmployeeId, selectedMonth]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      const endOfWeek = new Date(today);
      endOfWeek.setDate(today.getDate() + (6 - today.getDay()));

      // Format dates for API
      const todayStr = today.toISOString().split('T')[0];
      const weekStartStr = startOfWeek.toISOString().split('T')[0];
      const weekEndStr = endOfWeek.toISOString().split('T')[0];

      console.log('📅 Date Range Debug:');
      console.log('📅 Today:', todayStr);
      console.log('📅 Week Start:', weekStartStr);
      console.log('📅 Week End:', weekEndStr);
      console.log('📅 Current Employee ID:', currentEmployeeId);

      // Load all data in parallel using existing APIs
      const [
        todaysBookingsData,
        weeklyBookingsData,
        customersData,
        roomsData,
        equipmentsData,
        spaServicesData
      ] = await Promise.all([
        // Today's bookings
        getBookings({
          employee_Id: currentEmployeeId,
          from_Date: todayStr,
          to_Date: todayStr
        }).catch(() => []),
        
        // Weekly bookings for customer info
        getBookings({
          employee_Id: currentEmployeeId,
          from_Date: null,
          to_Date: null
        }).catch(() => []),
        
        // Customer data
        getCustomers({}).catch(() => []),
        
        // Room data
        getRooms({}).catch(() => []),
        
        // Equipment data
        getEquipments({}).catch(() => []),
        
        // Spa services data
        getSpaServices({}).catch(() => [])
      ]);

      console.log('📊 API Data Debug:');
      console.log('📊 Today Bookings:', todaysBookingsData);
      console.log('📊 Weekly Bookings (Raw):', weeklyBookingsData);
      
      // For now, use all bookings to test customer processing
      const filteredWeeklyBookings = weeklyBookingsData;
      
      console.log('📊 Weekly Bookings (Using All):', filteredWeeklyBookings);
      console.log('📊 Customers Data:', customersData);
      console.log('📊 Rooms Data:', roomsData);
      console.log('📊 Equipment Data:', equipmentsData);
      console.log('📊 Spa Services Data:', spaServicesData);

      // Process dashboard data
      const processedData = processDashboardData(
        todaysBookingsData,
        filteredWeeklyBookings, // Use filtered bookings instead of raw
        customersData,
        roomsData,
        equipmentsData,
        spaServicesData
      );

      setDashboardData(processedData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMonthBookings = async () => {
    try {
      const startOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
      const endOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0);
      
      const startStr = startOfMonth.toISOString().split('T')[0];
      const endStr = endOfMonth.toISOString().split('T')[0];

      const bookings = await getBookings({
        from_Date: startStr,
        to_Date: endStr
      }).catch(() => []);

      // Filter bookings for current employee (same logic as SpaCalendar)
      const employeeBookings = bookings.filter(booking => {
        if (booking.details) {
          return booking.details.some(detail => 
            detail.resourceDetails && detail.resourceDetails.some(resource => 
              resource.employee_Id === currentEmployeeId
            )
          );
        }
        return false;
      });

      setMonthBookings(employeeBookings);
    } catch (error) {
      console.error('Error loading month bookings:', error);
    }
  };

  const loadIncentiveData = async () => {
    try {
      const data = await getEmployeeIncentives();
      setIncentiveData(data);
    } catch (error) {
      console.error('Error loading incentive data:', error);
    }
  };

  const processDashboardData = (
    todaysBookings,
    weeklyBookings,
    customersData,
    roomsData,
    equipmentsData,
    spaServicesData
  ) => {
    // Process today's bookings
    const todaysBookingsProcessed = todaysBookings.map(booking => {
      // Get room ID from resourceDetails
      let roomId = null;
      if (booking.details && booking.details.length > 0) {
        const resourceDetails = booking.details[0]?.resourceDetails;
        if (resourceDetails && resourceDetails.length > 0) {
          roomId = resourceDetails[0].room_Id;
        }
      }
      
      return {
        ...booking,
        customerName: customersData.find(c => c.customer_Id === booking.customer_Id)?.customer_NameEn || 'Unknown',
        roomName: roomsData.find(r => r.room_Id === roomId)?.room_Name_English || 'Unknown',
        serviceDetails: getServiceDetails(booking, spaServicesData)
      };
    });

    // Calculate current status
    const currentTime = new Date();
    const currentBooking = todaysBookingsProcessed.find(booking => {
      const fromTime = new Date(booking.from_Time);
      const toTime = new Date(booking.to_Time);
      return currentTime >= fromTime && currentTime <= toTime;
    });
    const currentStatus = currentBooking ? 'Busy' : 'Available';

    // Remove performance metrics calculation

    // Process customer information
    console.log('👥 Customer Processing Debug:');
    console.log('👥 Weekly Bookings Count:', weeklyBookings.length);
    console.log('👥 Weekly Bookings Data:', weeklyBookings);
    
    const customerBookings = weeklyBookings.reduce((acc, booking) => {
      const customerId = booking.customer_Id;
      if (!acc[customerId]) {
        acc[customerId] = [];
      }
      acc[customerId].push(booking);
      return acc;
    }, {});

    console.log('👥 Customer Bookings Grouped:', customerBookings);
    console.log('👥 Customer IDs:', Object.keys(customerBookings));

    const recentCustomers = Object.keys(customerBookings)
      .map(customerId => {
        const customer = customersData.find(c => c.customer_Id === parseInt(customerId));
        const bookings = customerBookings[customerId];
        console.log(`👥 Processing Customer ${customerId}:`, {
          customer,
          bookings: bookings.length,
          customerName: customer?.customer_NameEn
        });
        return {
          customer_Id: parseInt(customerId),
          customer_NameEn: customer?.customer_NameEn || 'Unknown',
          bookingCount: bookings.length,
          lastBooking: bookings.sort((a, b) => new Date(b.booking_Date) - new Date(a.booking_Date))[0]
        };
      })
      .sort((a, b) => b.bookingCount - a.bookingCount)
      .slice(0, 5);

    console.log('👥 Recent Customers Final:', recentCustomers);

    const repeatCustomers = recentCustomers.filter(customer => customer.bookingCount > 1);
    console.log('👥 Repeat Customers:', repeatCustomers);

    // Process equipment and resources
    const assignedEquipment = [];
    const roomAssignments = [];

    todaysBookingsProcessed.forEach(booking => {
      if (booking.details) {
        booking.details.forEach(detail => {
          if (detail.resourceDetails) {
            detail.resourceDetails.forEach(resource => {
              if (resource.equipment_Id) {
                const equipment = equipmentsData.find(e => e.eq_Id === resource.equipment_Id);
                if (equipment && !assignedEquipment.find(e => e.eq_Id === equipment.eq_Id)) {
                  assignedEquipment.push({
                    ...equipment,
                    completionStatus: resource.completionStatus
                  });
                }
              }
              if (resource.room_Id) {
                const room = roomsData.find(r => r.room_Id === resource.room_Id);
                if (room && !roomAssignments.find(r => r.room_Id === room.room_Id)) {
                  roomAssignments.push({
                    ...room,
                    completionStatus: resource.completionStatus
                  });
                }
              }
            });
          }
        });
      }
    });

    return {
      todaysBookings: todaysBookingsProcessed,
      currentStatus,
      recentCustomers,
      repeatCustomers,
      assignedEquipment,
      roomAssignments
    };
  };

  const getServiceDetails = (booking, spaServicesData) => {
    if (!booking.details) return [];
    
    return booking.details.flatMap(detail => 
      detail.resourceDetails?.map(resource => {
        const service = spaServicesData.find(s => s.spaService_Id === resource.service_Id);
        return {
          serviceName: service?.spaServiceName_En || 'Unknown Service',
          duration: service?.duration || 0,
          completionStatus: resource.completionStatus || 'Pending'
        };
      }) || []
    );
  };

  const formatTime = (dateTime) => {
    return new Date(dateTime).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Available': { color: 'success', icon: CheckCircle, bgColor: '#d4edda', textColor: '#155724' },
      'Busy': { color: 'warning', icon: Clock, bgColor: '#fff3cd', textColor: '#856404' },
      'Complete': { color: 'success', icon: CheckCircle, bgColor: '#d4edda', textColor: '#155724' },
      'Pending': { color: 'warning', icon: AlertCircle, bgColor: '#fff3cd', textColor: '#856404' }
    };
    
    const config = statusConfig[status] || statusConfig['Pending'];
    const Icon = config.icon;
    
    return (
      <span 
        className="d-flex align-items-center gap-1"
        style={{
          backgroundColor: config.bgColor,
          color: config.textColor,
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'normal',
          border: '1px solid rgba(0,0,0,0.1)'
        }}
      >
        <Icon size={12} />
        {status}
      </span>
    );
  };

  const getBookingCountForDate = (date) => {
    // Use local date formatting to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    return monthBookings.filter(booking => {
      if (!booking.booking_Date) return false;
      const bookingDateStr = booking.booking_Date.split('T')[0];
      return bookingDateStr === dateStr;
    }).length;
  };

  const handleDateClick = (date) => {
    navigate('/GmsSpaCalendar');
  };

  const renderMiniCalendar = () => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const currentDate = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      const bookingCount = getBookingCountForDate(currentDate);
      const isCurrentMonth = currentDate.getMonth() === month;
      const isToday = currentDate.toDateString() === new Date().toDateString();
      
      days.push(
        <div
          key={i}
          className={`calendar-day ${isCurrentMonth ? 'current-month' : 'other-month'} ${isToday ? 'today' : ''}`}
          onClick={() => handleDateClick(currentDate)}
          style={{
            cursor: 'pointer',
            padding: '5px',
            border: '1px solid #dee2e6',
            minHeight: '40px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: isToday ? '#e3f2fd' : 'white',
            opacity: isCurrentMonth ? 1 : 0.5
          }}
        >
          <div style={{ fontSize: '12px', fontWeight: isToday ? 'bold' : 'normal' }}>
            {currentDate.getDate()}
          </div>
          {bookingCount > 0 && (
            <Badge 
              color="primary" 
              style={{ 
                fontSize: '10px', 
                marginTop: '2px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none'
              }}
            >
              {bookingCount}
            </Badge>
          )}
        </div>
      );
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  };

  if (!hasPermission) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Alert color="danger">
            You don't have permission to access this page.
          </Alert>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <ClipLoader color="#556ee6" size={50} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Spa Employee Dashboard" breadcrumbItem="Dashboard" />
        
        <Row>
          {/* Today's Schedule */}
          <Col lg={8}>
            <Card>
              <CardHeader>
                <CardTitle className="d-flex align-items-center gap-2">
                  <Calendar className="text-primary" size={20} />
                  Today's Schedule
                </CardTitle>
              </CardHeader>
              <CardBody>
                {dashboardData.todaysBookings.length > 0 ? (
                  <div className="table-responsive">
                    <Table className="table-nowrap">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Customer</th>
                          <th>Service</th>
                          <th>Room</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.todaysBookings
                          .sort((a, b) => new Date(a.from_Time) - new Date(b.from_Time))
                          .map((booking, index) => (
                            <tr key={index}>
                              <td>
                                <div className="d-flex align-items-center gap-1">
                                  <Clock size={14} />
                                  {formatTime(booking.from_Time)} - {formatTime(booking.to_Time)}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-1">
                                  <User size={14} />
                                  {booking.customerName}
                                </div>
                              </td>
                              <td>
                                {booking.serviceDetails.map((service, idx) => (
                                  <div key={idx} className="small">
                                    {service.serviceName}
                                  </div>
                                ))}
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-1">
                                  <MapPin size={14} />
                                  {booking.roomName}
                                </div>
                              </td>
                              <td>
                                {booking.serviceDetails && booking.serviceDetails.length > 0 ? (
                                  booking.serviceDetails.map((service, idx) => (
                                    <div key={idx}>
                                      {getStatusBadge(service.completionStatus || 'Pending')}
                                    </div>
                                  ))
                                ) : (
                                  <div>
                                    {getStatusBadge('Pending')}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert color="info">
                    No bookings scheduled for today.
                  </Alert>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Recent Customers */}
          <Col lg={4}>
            <Card>
              <CardHeader>
                <CardTitle className="d-flex align-items-center gap-2">
                  <Users className="text-info" size={20} />
                  Recent Customers
                </CardTitle>
              </CardHeader>
              <CardBody>
                {dashboardData.recentCustomers.length > 0 ? (
                  <div className="table-responsive">
                    <Table className="table-sm">
                      <tbody>
                        {dashboardData.recentCustomers.map((customer, index) => (
                          <tr key={index}>
                            <td>
                              <div className="d-flex align-items-center gap-1">
                                <User size={14} />
                                {customer.customer_NameEn}
                              </div>
                            </td>
                            <td>
                              <Badge color="primary">{customer.bookingCount}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert color="info" className="mb-0">
                    No recent customers.
                  </Alert>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Incentive Cards */}
        <Row className="mt-3">
          <Col lg={6}>
            <Card>
              <CardHeader>
                <CardTitle className="d-flex align-items-center gap-2">
                  <div style={{ 
                    width: '20px', 
                    height: '20px', 
                    backgroundColor: '#28a745', 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    P
                  </div>
                  Performance Incentive
                </CardTitle>
              </CardHeader>
              <CardBody>
                <div className="text-center">
                  <h3 className="text-success mb-2">
                    {incentiveData.performance_Incentive?.toFixed(2) || '0.00'}
                  </h3>
                  <p className="text-muted small mb-0">
                    {incentiveData.performance_Message || 'Based on services completed'}
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={6}>
            <Card>
              <CardHeader>
                <CardTitle className="d-flex align-items-center gap-2">
                  <div style={{ 
                    width: '20px', 
                    height: '20px', 
                    backgroundColor: '#007bff', 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    S
                  </div>
                  Sales Incentive
                </CardTitle>
              </CardHeader>
              <CardBody>
                <div className="text-center">
                  <h3 className="text-primary mb-2">
                    {incentiveData.sales_Incentive?.toFixed(2) || '0.00'}
                  </h3>
                  <p className="text-muted small mb-0">
                    {incentiveData.sales_Message || 'Based on products sold'}
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Total Incentive Summary */}
        <Row className="mt-3">
          <Col lg={12}>
            <Card>
              <CardHeader>
                <CardTitle className="d-flex align-items-center gap-2">
                  <div style={{ 
                    width: '20px', 
                    height: '20px', 
                    backgroundColor: '#6f42c1', 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    T
                  </div>
                  Total Incentives (Last 30 Days)
                </CardTitle>
              </CardHeader>
              <CardBody>
                <div className="text-center">
                  <h2 className="text-purple mb-2">
                    {incentiveData.total_Incentive?.toFixed(2) || '0.00'}
                  </h2>
                  <p className="text-muted small mb-0">
                    Performance: {incentiveData.performance_Incentive?.toFixed(2) || '0.00'} + Sales: {incentiveData.sales_Incentive?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Equipment & Resources */}
        <Row className="mt-3">
          <Col lg={6}>
            <Card>
              <CardHeader>
                <CardTitle className="d-flex align-items-center gap-2">
                  <Wrench className="text-warning" size={20} />
                  Assigned Equipment
                </CardTitle>
              </CardHeader>
              <CardBody>
                {dashboardData.assignedEquipment.length > 0 ? (
                  <div className="table-responsive">
                    <Table className="table-sm">
                      <tbody>
                        {dashboardData.assignedEquipment.map((equipment, index) => (
                          <tr key={index}>
                            <td>
                              <div className="d-flex align-items-center gap-1">
                                <Wrench size={14} />
                                {equipment.eq_Name_English}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert color="info" className="mb-0">
                    No equipment assigned for today.
                  </Alert>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col lg={6}>
            <Card>
              <CardHeader>
                <CardTitle className="d-flex align-items-center gap-2">
                  <MapPin className="text-danger" size={20} />
                  Room Assignments
                </CardTitle>
              </CardHeader>
              <CardBody>
                {dashboardData.roomAssignments.length > 0 ? (
                  <div className="table-responsive">
                    <Table className="table-sm">
                      <tbody>
                        {dashboardData.roomAssignments.map((room, index) => (
                          <tr key={index}>
                            <td>
                              <div className="d-flex align-items-center gap-1">
                                <MapPin size={14} />
                                {room.room_Name_English}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert color="info" className="mb-0">
                    No room assignments for today.
                  </Alert>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Mini Calendar View */}
        <Row className="mt-3">
          <Col lg={12}>
            <Card>
              <CardHeader>
                <CardTitle className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <Calendar className="text-primary" size={20} />
                    My Bookings Calendar
                  </div>
                  <div className="d-flex gap-2">
                    <Button 
                      color="outline-secondary" 
                      size="sm" 
                      onClick={() => {
                        const prevMonth = new Date(selectedMonth);
                        prevMonth.setMonth(prevMonth.getMonth() - 1);
                        setSelectedMonth(prevMonth);
                      }}
                    >
                      ←
                    </Button>
                    <span className="align-self-center">
                      {selectedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                    <Button 
                      color="outline-secondary" 
                      size="sm" 
                      onClick={() => {
                        const nextMonth = new Date(selectedMonth);
                        nextMonth.setMonth(nextMonth.getMonth() + 1);
                        setSelectedMonth(nextMonth);
                      }}
                    >
                      →
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardBody>
                <div className="mini-calendar">
                  <div className="calendar-header" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', marginBottom: '5px' }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} style={{ textAlign: 'center', fontWeight: 'bold', padding: '5px', fontSize: '12px' }}>
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className="calendar-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px' }}>
                    {renderMiniCalendar()}
                  </div>
                  <div className="text-center mt-2">
                    <small className="text-muted">Click any date to view full calendar</small>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default SpaEmployeeDashboard;
